// Service de gestion des emails pour Résidences Masséna
class EmailService {
  constructor() {
    this.adminEmail = 's.massena@videotron.ca'
    this.publicEmail = 'admin@residencemassena.com'
  }

  // Simuler l'envoi d'email avec redirection automatique
  async sendEmail({ to, subject, body, type = 'general' }) {
    try {
      // Si l'email est envoyé à admin@residencemassena.com, rediriger vers s.massena@videotron.ca
      if (to === this.publicEmail || to.includes('admin@residencemassena.com')) {
        to = this.adminEmail
        console.log(`📧 Email redirigé de ${this.publicEmail} vers ${this.adminEmail}`)
      }

      // Simuler l'envoi d'email
      const emailData = {
        to,
        subject,
        body,
        type,
        timestamp: new Date().toISOString(),
        redirected: to === this.adminEmail && to !== this.publicEmail
      }

      // En production, ici on utiliserait un vrai service d'email comme SendGrid, Mailgun, etc.
      console.log('📧 Email envoyé:', emailData)
      
      // Simuler un délai d'envoi
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      return {
        success: true,
        messageId: `email_${Date.now()}`,
        redirected: emailData.redirected
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi d\'email:', error)
      throw error
    }
  }

  // Envoyer une notification d'approbation admin
  async sendAdminApprovalRequest(adminData) {
    const subject = `Demande d'ajout d'administrateur - ${adminData.firstName} ${adminData.lastName}`
    const body = this.generateAdminApprovalEmailBody(adminData)
    
    return this.sendEmail({
      to: this.adminEmail,
      subject,
      body,
      type: 'admin_approval'
    })
  }

  // Envoyer une notification de nouvelle candidature
  async sendJobApplicationNotification(applicationData) {
    const subject = `Nouvelle candidature - ${applicationData.jobTitle}`
    const body = this.generateJobApplicationEmailBody(applicationData)
    
    return this.sendEmail({
      to: this.adminEmail,
      subject,
      body,
      type: 'job_application'
    })
  }

  // Envoyer une notification de message de contact
  async sendContactMessageNotification(messageData) {
    const subject = `Nouveau message de contact - ${messageData.name}`
    const body = this.generateContactMessageEmailBody(messageData)
    
    return this.sendEmail({
      to: this.adminEmail,
      subject,
      body,
      type: 'contact_message'
    })
  }

  // Générer le corps de l'email d'approbation admin
  generateAdminApprovalEmailBody(adminData) {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2563eb;">Demande d'ajout d'administrateur</h2>
        
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Informations du candidat</h3>
          <p><strong>Nom complet:</strong> ${adminData.firstName} ${adminData.lastName}</p>
          <p><strong>Email:</strong> ${adminData.email}</p>
          <p><strong>Rôle demandé:</strong> ${adminData.role}</p>
        </div>

        <div style="background: #f0f9ff; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Permissions demandées</h3>
          <ul>
            ${adminData.permissions.banners ? '<li>✅ Gestion des bannières</li>' : '<li>❌ Gestion des bannières</li>'}
            ${adminData.permissions.jobs ? '<li>✅ Gestion des emplois</li>' : '<li>❌ Gestion des emplois</li>'}
            ${adminData.permissions.gallery ? '<li>✅ Gestion de la galerie</li>' : '<li>❌ Gestion de la galerie</li>'}
            ${adminData.permissions.applications ? '<li>✅ Gestion des candidatures</li>' : '<li>❌ Gestion des candidatures</li>'}
            ${adminData.permissions.settings ? '<li>✅ Paramètres</li>' : '<li>❌ Paramètres</li>'}
          </ul>
        </div>

        <div style="background: #fef3c7; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Action requise</h3>
          <p>Veuillez approuver ou rejeter cette demande d'ajout d'administrateur.</p>
          <div style="margin-top: 15px;">
            <a href="#" style="background: #10b981; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; margin-right: 10px;">✅ Approuver</a>
            <a href="#" style="background: #ef4444; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">❌ Rejeter</a>
          </div>
        </div>

        <p style="color: #6b7280; font-size: 14px;">
          Email automatique généré par le système Résidences Masséna
        </p>
      </div>
    `
  }

  // Générer le corps de l'email de candidature
  generateJobApplicationEmailBody(applicationData) {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2563eb;">Nouvelle candidature d'emploi</h2>
        
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Informations du candidat</h3>
          <p><strong>Nom:</strong> ${applicationData.name}</p>
          <p><strong>Email:</strong> ${applicationData.email}</p>
          <p><strong>Téléphone:</strong> ${applicationData.phone || 'Non fourni'}</p>
          <p><strong>Poste:</strong> ${applicationData.jobTitle}</p>
        </div>

        ${applicationData.message ? `
          <div style="background: #f0f9ff; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Message du candidat</h3>
            <p>${applicationData.message}</p>
          </div>
        ` : ''}

        <p style="color: #6b7280; font-size: 14px;">
          Email automatique généré par le système Résidences Masséna
        </p>
      </div>
    `
  }

  // Générer le corps de l'email de message de contact
  generateContactMessageEmailBody(messageData) {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2563eb;">Nouveau message de contact</h2>
        
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Informations du contact</h3>
          <p><strong>Nom:</strong> ${messageData.name}</p>
          <p><strong>Email:</strong> ${messageData.email}</p>
          <p><strong>Téléphone:</strong> ${messageData.phone || 'Non fourni'}</p>
          <p><strong>Sujet:</strong> ${messageData.subject}</p>
        </div>

        <div style="background: #f0f9ff; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Message</h3>
          <p>${messageData.message}</p>
        </div>

        <p style="color: #6b7280; font-size: 14px;">
          Email automatique généré par le système Résidences Masséna
        </p>
      </div>
    `
  }
}

// Instance singleton
const emailService = new EmailService()

export default emailService
